import React, { useMemo, useState } from 'react';
import cn from 'classnames';
import { noop } from 'lodash';
import moment from 'moment';

import { beGetSalonTaxesAndFees } from 'actions/salon/beGetSalonTaxesAndFees.action';
import DndDraggable from 'components/dnd/DndDraggable.component';
import DndDroppable from 'components/dnd/DndDroppable.component';
import Button from 'components/form/Button.component';
import { usePrepareReadOnly } from 'hooks/usePrepare.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import logger from 'utils/logger.util';
import dnd from 'utils/react/dnd.util';

import PopupModalContainer from 'common/PopupModal';
import { AddService } from 'onboarding/services/AddService';
import ServiceListItem from 'onboarding/services/ServiceListItem';

import { EditServiceSection } from './EditServiceSection';

import CN from './CategoryItem.module.scss';

const L = logger('CategoryItem');

export const CategoryItem = ({
  settingsSavedServicesIDs,
  isAddNewServiceExpanded: isExpandedFromProps,
  isExpandable,
  onDelete,
  expandedServicesIDs,
  category,
  onEditServiceSave,
  onEdit,
  salonType,
  quickSetupLayout,
  dragAndDropEnabled,
  settingsErrorServicesIDs,
  onAddNewService,
}) => {
  const defaultService = {
    name: '',
    category: category.id ? category.id : '',
    duration: moment('00:45:00', 'HH:mm:ss').format('HH:mm:ss'),
    processing: moment('00:00:00', 'HH:mm:ss').format('HH:mm:ss'),
    variable_pricing: false,
    price: salonType === 'service' ? '' : '40',
    role_executions: [],
  };

  const { qj: taxesAndFees } = usePrepareReadOnly(beGetSalonTaxesAndFees);

  const [newService, setNewService] = useState(defaultService);
  const services = useMemo(() => category.services ?? [], [category.services]);
  const [errorMessage, setErrorMessage] = useState('');
  const [isAddNewServiceExpanded, setIsAddNewServiceExpanded] = useState(false);
  const [isNewService, setIsNewService] = useState(false);
  const [isFirstPriceChange, setIsFirstPriceChange] = useState(false);
  const [isFirstDurationChange, setIsFirstDurationChange] = useState(false);
  const [isFirstProcessingChange, setIsFirstProcessingChange] = useState(false);
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const taxes = useMemo(() => taxesAndFees?.taxes ?? [], [taxesAndFees]);
  const fees = useMemo(() => taxesAndFees?.fees ?? [], [taxesAndFees]);

  const servicesTaxes = taxes?.filter((tax) => tax.applied_to === 'services' || tax.applied_to === 'products_and_services');
  const servicesFees = fees?.filter((fee) => fee.applied_to === 'services' || fee.applied_to === 'products_and_services');

  const changeServiceName = (value) => {
    setNewService((prev) => ({ ...prev, name: value }));
  };

  // toggle role behavior
  const changeRole = (roleId) => {
    // if it is already in role_executions []  remove it
    const newRoleExecutions = [...newService.role_executions];
    const existingIndex = newRoleExecutions.findIndex((role) => role.role_id === roleId);
    // if role already exists -> remove it
    if (existingIndex !== -1) {
      newRoleExecutions.splice(existingIndex, 1);
    } else {
      // add it
      newRoleExecutions.push({
        role_id: roleId,
        price: '',
        duration: '00:45:00',
        processing: '00:00:00',
      });
    }
    // always update state
    setNewService((prev) => ({ ...prev, role_executions: newRoleExecutions }));
  };

  const changeDuration = (value) => {
    const formattedDuration = moment(value, 'HH:mm').format('HH:mm:ss');
    setNewService((prev) => ({ ...prev, duration: formattedDuration }));
  };

  const changeProcessing = (value) => {
    const formattedProcessing = moment(value, 'HH:mm').format('HH:mm:ss');
    setNewService((prev) => ({ ...prev, processing: formattedProcessing }));
  };

  const changePrice = (value) => {
    setNewService((prev) => ({ ...prev, price: value }));
  };

  const changeVariablePricing = (value) => {
    setNewService((prev) => ({ ...prev, variable_pricing: value }));
  };

  const onRolePriceBlur = (value) => {
    const editedService = { ...newService };
    if (isNewService && isFirstPriceChange) {
      editedService.role_executions.forEach((role) => {
        role.price = value;
      });
      editedService.price = value;
      setNewService(editedService);
      setIsFirstPriceChange(false);
    }
  };

  const onRolePriceChange = (roleId, value) => {
    const editedService = { ...newService };
    editedService.role_executions.forEach((role) => {
      if (role.role_id === roleId) {
        role.price = value;
        editedService.price = value;
      }
    });
    editedService.price = value;
    setNewService(editedService);
  };

  const onRoleApplicationChange = (roleId, value) => {
    const editedService = { ...newService };
    if (isNewService && isFirstDurationChange) {
      editedService.role_executions.forEach((role) => {
        role.duration = moment(value, 'HH:mm').format('HH:mm:ss');
      });
      editedService.duration = value;
      setNewService(editedService);
      setIsFirstDurationChange(false);
      return;
    }
    editedService.role_executions.forEach((role) => {
      if (role.role_id === roleId) {
        role.duration = moment(value, 'HH:mm').format('HH:mm:ss');
        editedService.duration = value;
      }
    });
    editedService.duration = value;
    setNewService(editedService);
  };

  const onRoleProcessingChange = (roleId, value) => {
    const editedService = { ...newService };
    if (isNewService && isFirstProcessingChange) {
      editedService.role_executions.forEach((role) => {
        role.processing = moment(value, 'HH:mm').format('HH:mm:ss');
      });
      editedService.processing = value;
      setNewService(editedService);
      setIsFirstProcessingChange(false);
      return;
    }

    editedService.role_executions.forEach((role) => {
      if (role.role_id === roleId) {
        role.processing = moment(value, 'HH:mm').format('HH:mm:ss');
      }
    });
    editedService.processing = value;
    setNewService(editedService);
  };

  const onAddNewServiceBtnClick = () => {
    if (onAddNewService && validateService(newService)) {
      // Fills in the default taxes that were not manually selected from the UI
      const taxes = servicesTaxes ?? [];
      const defaultTaxes = taxes.filter((tax) => tax.is_default);
      const selectedTaxes = newService?.tax_ids ?? {};

      const taxIds = Object.entries(selectedTaxes)
        .filter(([_, value]) => !!value)
        .map(([key]) => parseInt(key));

      const newTaxes = [...taxIds];

      defaultTaxes?.forEach((defaultTax) => {
        if (selectedTaxes[defaultTax.id] === undefined) {
          newTaxes.push(defaultTax.id);
        }
      });

      const fees = servicesFees ?? [];
      const defaultFees = fees.filter((fee) => fee.is_default);
      const selectedFees = newService?.fee_ids ?? {};

      const feeIds = Object.entries(selectedFees)
        .filter(([_, value]) => !!value)
        .map(([key]) => parseInt(key));

      const newFees = [...feeIds];

      defaultFees?.forEach((defaultFee) => {
        if (selectedFees[defaultFee.id] === undefined) {
          newFees.push(defaultFee.id);
        }
      });

      onAddNewService(
        {
          ...newService,
          tax_ids: newTaxes,
          fee_ids: newFees,
          is_tax_exempt: newService?.is_tax_exempt || false,
          tax_exempt_reason: newService?.tax_exempt_reason || '',
        },
        category,
      );
      setIsAddNewServiceExpanded(false);
      setNewService(defaultService);
    }
  };

  const showAddServiceDropdown = () => {
    setIsAddNewServiceExpanded(true);
    setIsFirstDurationChange(true);
    setIsFirstProcessingChange(true);
    setIsFirstPriceChange(true);
    setIsNewService(true);
    resetService();
  };

  const selectTax = (taxes) => {
    setNewService((prev) => ({ ...prev, tax_ids: taxes }));
  };

  const selectFee = (fees) => {
    setNewService((prev) => ({ ...prev, fee_ids: fees }));
  };

  const toggleExempt = (isExempt) => {
    setNewService((prev) => ({ ...prev, is_tax_exempt: isExempt, tax_exempt_reason: !isExempt ? '' : prev.tax_exempt_reason }));
  };

  const changeReason = (reason) => {
    setNewService((prev) => ({ ...prev, tax_exempt_reason: reason }));
  };

  const validateService = (service) => {
    L.debug('validateService()', service);

    if (service.is_tax_exempt === true && !service.tax_exempt_reason?.trim()) {
      return false;
    }

    if (service.name && (salonType === 'service' ? service.price !== '' : true) && service.duration !== '00:00:00') {
      return !service.role_executions.some((role_execution) => role_execution.price === '');
    }

    return false;
  };

  const assignNewCategory = (service, category) => {
    if (!category.id) {
      return;
    }

    service.category = category.id;
  };

  const hideError = () => {
    setErrorMessage('');
  };

  const resetService = () => {
    setNewService(defaultService);
  };

  const { name: categoryName, id: categoryId } = category;
  const buttonName = services?.length > 0 ? t('composite.categoryItem.addAnotherServiceBtn') : t('composite.categoryItem.addAnotherServiceBtn', { category: categoryName });

  // If there are multiple services, we allow only one service to be expanded
  const editedServicePosition = services.findIndex(({ id }) => expandedServicesIDs.includes(id));
  const hasEditedService = editedServicePosition >= 0;

  return (
    <div
      data-bem="CategoryItem"
      className={cn({
        'category-item': true,
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      {!isExpandable && (
        <div className="category-wrap">
          <div style={{ backgroundColor: category.color }} className="category-bullet" />
          <div className="category-text">{categoryName}</div>
        </div>
      )}

      <DndDroppable isDropDisabled={!dragAndDropEnabled || hasEditedService} tag="CategoryItem--dnd-target" droppableId={dnd.dropService({ categoryId })}>
        {({ innerRef, placeholder }) => (
          <div data-bem="CategoryItem--el-target" ref={innerRef}>
            {dragAndDropEnabled && !hasEditedService && placeholder}
            <div data-bem="CategoryItem__category--jsx" className="rounded-border-wrap dashed">
              {services &&
                services.map((service, i) => {
                  if (hasEditedService && i > editedServicePosition) return null;
                  const serviceId = service.id;
                  const expanded = expandedServicesIDs.indexOf(serviceId) !== -1;
                  const isSaved = settingsSavedServicesIDs.indexOf(serviceId) !== -1;
                  const settingsError = settingsErrorServicesIDs.find(({ id }) => serviceId === id);

                  return (
                    <div key={serviceId} id={serviceId}>
                      {errorMessage && <PopupModalContainer popupTitle={t('common.error')} popupMessage={errorMessage} buttonText={t('common.buttons.okay')} buttonCallback={hideError} />}
                      <DndDraggable isDragDisabled={!dragAndDropEnabled || hasEditedService} tag="CategoryItem--dnd-source" draggableId={dnd.dragService({ categoryId, serviceId })} index={i}>
                        {({ dragHandleProps, draggableProps, innerRef }) => (
                          <div data-bem="CategoryItem--el-source" ref={innerRef} {...draggableProps} {...dragHandleProps} style={draggableProps.style}>
                            <div data-bem="CategoryItem__service--jsx" className="obs-edit-container cfix">
                              <ServiceListItem
                                expandable={isExpandable}
                                showDotsMenu={!expanded}
                                quickSetupLayout={quickSetupLayout}
                                itemName={service.name}
                                isSaved={isSaved}
                                errorMessage={settingsError && settingsError.message}
                                onEdit={() => onEdit(service, category)}
                                onDelete={() => onDelete(serviceId)}
                              />
                              {expanded && <EditServiceSection service={service} category={category} quickSetupLayout={quickSetupLayout} onEditServiceSave={onEditServiceSave} />}
                            </div>
                          </div>
                        )}
                      </DndDraggable>
                    </div>
                  );
                })}
              {!isExpandedFromProps && !quickSetupLayout && !hasEditedService && (
                <div className={CN.buttonContainer}>
                  <Button data-bem="CategoryItem__add-new" xSpace noMargin width="max" variant="primaryForce" onClick={showAddServiceDropdown}>
                    {buttonName}
                  </Button>
                </div>
              )}
              {(isExpandedFromProps || isAddNewServiceExpanded) && (
                <div className="first-service-container">
                  <button className="close-btn" onClick={() => setIsAddNewServiceExpanded(false)} />

                  <AddService
                    service={newService}
                    category={category}
                    isNameSelected
                    serviceTemplates={[]}
                    settingsLayout
                    onServiceNameChange={changeServiceName}
                    onCategoryChange={noop}
                    onCategoryChangeInSettings={assignNewCategory}
                    onRoleCheckboxChange={changeRole}
                    onRolePriceChange={onRolePriceChange}
                    onRolePriceBlur={onRolePriceBlur}
                    onRoleApplicationChange={onRoleApplicationChange}
                    onRoleProcessingChange={onRoleProcessingChange}
                    onDurationChange={changeDuration}
                    onProcessingChange={changeProcessing}
                    onPriceChange={changePrice}
                    onPrincingChange={changeVariablePricing}
                    addNewService={noop}
                    selectTemplate={noop}
                    onSelectTax={selectTax}
                    onSelectFee={selectFee}
                    onToggleExempt={(isExempt) => toggleExempt(isExempt)}
                    onChangeReason={changeReason}
                  />
                  {newService.name.length <= 0 && <span className={CN.error}>{t('composite.categoryItem.serviceNameErrorMsg')}</span>}

                  <div className={CN.buttonContainer}>
                    <Button
                      data-bem="CategoryItem__save-button"
                      xSpace
                      noMargin
                      width="max"
                      variant="primaryForce"
                      onClick={onAddNewServiceBtnClick}
                      disabled={!validateService(newService) || newService.name.length <= 0}
                    >
                      {t('composite.categoryItem.saveNewServButton')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </DndDroppable>
    </div>
  );
};

export default CategoryItem;
